/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { AdaptiveFormShell, Form } from '@/shared/ui/components/form';
import { cepMask } from '@/shared/utils/input-masks';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { ProfileField, ProfileDrawerSubmitButton } from './profile-drawer-parts';
import { useSaveAgencyProfile } from './use-save-agency-profile';

const addressSchema = z.object({
  postal_code: z.string().optional(),
  street: z.string().optional(),
  number: z.string().optional(),
  complement: z.string().optional(),
  neighborhood: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
});

type AddressFormData = z.infer<typeof addressSchema>;

/**
 * Renders a drawer for editing the agency postal address.
 *
 * @param props - The drawer props.
 * @param props.open - Whether the drawer is visible.
 * @param props.onClose - Callback invoked to close the drawer.
 * @returns The address drawer element.
 */
export function AddressDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation('features/agency-settings');
  const { agency } = useAgency((s) => s);
  const save = useSaveAgencyProfile(onClose);

  const values: AddressFormData = {
    postal_code: agency?.postal_code ?? '',
    street: agency?.street ?? '',
    number: agency?.number ?? '',
    complement: agency?.complement ?? '',
    neighborhood: agency?.neighborhood ?? '',
    city: agency?.city ?? '',
    state: agency?.state ?? '',
  };

  const {
    control, handleSubmit, reset,
    formState: { isSubmitting },
  } = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    values,
  });

  // Discards edits abandoned on a previous open, and picks up the agency
  // record when it arrives after this drawer has already mounted.
  useEffect(() => {
    if (open) reset(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agency?.id]);

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      density="compact"
      title={t('v2.profile.changeAddress')}
      footer={
        <ProfileDrawerSubmitButton
          form="address-form"
          disabled={isSubmitting}
          label={t('v2.profile.save')}
          savingLabel={t('v2.profile.saving')}
        />
      }
    >
      <Form
        id="address-form"
        onSubmit={handleSubmit(save)}
        className="grid grid-cols-1 md:grid-cols-2 gap-(--space-4)"
      >
        <ProfileField
          control={control}
          name="postal_code"
          label={t('agencyData.labels.cep')}
          mask={cepMask}
        />
        <ProfileField control={control} name="street" label={t('agencyData.labels.rua')} />
        <ProfileField control={control} name="number" label={t('agencyData.labels.numero')} />
        <ProfileField
          control={control}
          name="complement"
          label={t('agencyData.labels.complemento')}
        />
        <ProfileField
          control={control}
          name="neighborhood"
          label={t('agencyData.labels.bairro')}
        />
        <ProfileField control={control} name="city" label={t('agencyData.labels.cidade')} />
        <ProfileField control={control} name="state" label={t('agencyData.labels.estado')} />
      </Form>
    </AdaptiveFormShell>
  );
}
